import React, { useCallback, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ApperIcon from "@/components/ApperIcon";
import { useAuth } from "@/layouts/Root";
import { cn } from "@/utils/cn";

const SIDEBAR_STORAGE_KEY = "sidebar-collapsed";

const Layout = () => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true"
  );
  const location = useLocation();
  const { logout } = useAuth();
  const { user } = useSelector(state => state.user);

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const handleCopyLink = () => {
    const publicUrl = window.location.origin + '/pubblico';
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

const navItems = [
    { to: "/", icon: "BarChart3", label: "Dashboard", exact: true },
    { to: "/spazi", icon: "MapPin", label: "Spazi Pubblicitari" },
    { to: "/clienti", icon: "Users", label: "Clienti" },
    { to: "/prenotazioni", icon: "Calendar", label: "Prenotazioni" },
    { to: "/timeline", icon: "CalendarDays", label: "Timeline" },
    { to: "/pagamenti", icon: "CreditCard", label: "Pagamenti" },
    { to: "/competenze-mensili", icon: "TrendingUp", label: "Competenze Mensili" },
    { to: "/preventivi", icon: "FileText", label: "Preventivi" },
    { divider: true },
    { to: "/pubblico", icon: "Globe", label: "Pagina Pubblica" },
  ];
  /* ── Sidebar inner content — shared by desktop & mobile ── */
  const renderSidebarContent = (collapsed = false) => (
    <div className="flex flex-col h-full">
      {/* Logo / Header */}
      <div
        className={cn(
          "py-4 sidebar-logo-border flex items-center",
          collapsed ? "px-0 justify-center" : "px-5 gap-3"
        )}
      >
<div className="relative shrink-0">
          <img
            src="/ps-logo.svg"
            alt="PubblicitaSoverato Logo"
            className="w-9 h-9 shrink-0 drop-shadow-lg object-contain"
            style={{ minWidth: '36px', filter: 'drop-shadow(0 0 8px rgba(124,58,237,0.45))' }}
          />
        </div>
        <div
          className={cn(
            "sidebar-label-fade flex flex-col min-w-0",
            collapsed ? "collapsed" : "expanded"
          )}
        >
          <span
            className="text-xs font-display font-bold tracking-tight truncate leading-tight"
            title="PUBBLICITASOVERATO"
            style={{
              background: 'linear-gradient(135deg, #e9e0ff 0%, #c4b5fd 50%, #a78bfa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '0.78rem',
            }}
          >
            PUBBLICITASOVERATO
          </span>
          <span className="text-mobile-xs" style={{ color: 'rgba(148, 130, 190, 0.5)', letterSpacing: '0.06em' }}>
            CRM Platform
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto" style={{ scrollbarWidth: 'none', paddingLeft: collapsed ? 0 : '12px', paddingRight: collapsed ? 0 : '12px' }}>
        <div className={cn("space-y-0.5", collapsed && "flex flex-col items-center")}>
          {navItems.map((item, index) => {
            if (item.divider) {
              if (collapsed) {
                return <div key={index} className="nav-divider-line" style={{ width: '32px', margin: '10px auto' }} />;
              }
              if (item.label) {
                return (
                  <div key={index} className="pt-4 pb-1">
                    <span className="nav-section-label">{item.label}</span>
                  </div>
                );
              }
              return <div key={index} className="nav-divider-line" />;
            }

            const isActive = item.exact
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to) && item.to !== "/";

            if (collapsed) {
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-xl transition-none",
                    isActive ? "nav-item-active" : "nav-item-inactive"
                  )}
                  onClick={() => setIsMobileNavOpen(false)}
                >
                  <ApperIcon
                    name={item.icon}
                    className={cn(
                      "w-4 h-4 shrink-0 transition-none",
                      isActive ? "opacity-100" : "opacity-70"
                    )}
                  />
                </NavLink>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                title={item.label}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-body-sm font-medium transition-none",
                  isActive ? "nav-item-active" : "nav-item-inactive"
                )}
                onClick={() => setIsMobileNavOpen(false)}
              >
                <ApperIcon
                  name={item.icon}
                  className={cn(
                    "w-4 h-4 shrink-0 transition-none",
                    isActive ? "opacity-100" : "opacity-70"
                  )}
                />
                <span className="truncate text-xs font-semibold tracking-wide">{item.label}</span>
                {item.badge != null && (
                  <span
                    className="ml-auto shrink-0 text-mobile-xs font-bold px-1.5 py-0.5 rounded-full"
                    style={{
                      background: 'rgba(124, 58, 237, 0.25)',
                      border: '1px solid rgba(167, 139, 250, 0.30)',
                      color: '#c4b5fd',
                      minWidth: '18px',
                      textAlign: 'center',
                    }}
                  >
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Toggle button — desktop only (collapsed prop is false for mobile) */}
      {collapsed !== "mobile" && (
        <div className={cn("px-3 py-2 flex", collapsed ? "justify-center" : "justify-end")}>
          <button
            type="button"
            onClick={toggleSidebar}
            title={collapsed ? "Espandi menu" : "Comprimi menu"}
            className="sidebar-toggle-btn w-8 h-8 flex items-center justify-center"
            style={{ padding: 0 }}
          >
            <ApperIcon
              name={collapsed ? "ChevronRight" : "ChevronLeft"}
              className="w-4 h-4 shrink-0"
              style={{
                transition: 'transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            />
          </button>
        </div>
      )}

      {/* User & Logout Footer */}
      <div className={cn(
        "pb-4 pt-3 sidebar-footer-border space-y-2",
        collapsed ? "px-1" : "px-3"
      )}>
        {user && (
          collapsed ? (
            <div
              className="flex items-center justify-center py-2"
              title={user.firstName || user.name || "Utente"}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: 'rgba(124, 58, 237, 0.25)',
                  border: '1px solid rgba(167, 139, 250, 0.30)',
                }}
              >
                <ApperIcon name="User" className="w-3.5 h-3.5" style={{ color: '#c4b5fd' }} />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-3 py-2.5 sidebar-user-card">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: 'rgba(124, 58, 237, 0.25)',
                  border: '1px solid rgba(167, 139, 250, 0.30)',
                }}
              >
                <ApperIcon name="User" className="w-3.5 h-3.5" style={{ color: '#c4b5fd' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate leading-tight" style={{ color: 'rgba(220, 210, 255, 0.9)' }}>
                  {user.firstName || user.name || "Utente"}
                </p>
                <p className="text-mobile-xs truncate leading-tight mt-0.5" style={{ color: 'rgba(148, 130, 190, 0.55)' }}>
                  {user.emailAddress || ""}
                </p>
              </div>
            </div>
          )
        )}
        <button
          type="button"
          onClick={logout}
          title="Esci"
          className={cn(
            "w-full flex items-center gap-3 rounded-xl text-body-sm font-medium sidebar-logout-btn",
            collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"
          )}
        >
          <ApperIcon name="LogOut" className="w-4 h-4 shrink-0 opacity-70" />
          <span
            className={cn(
              "sidebar-label-fade text-xs font-semibold tracking-wide",
              collapsed ? "collapsed" : "expanded"
            )}
          >
            Esci
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:z-sticky flex-col glass-sidebar sidebar-transition"
        style={{ width: isSidebarCollapsed ? '64px' : '240px' }}
      >
        {renderSidebarContent(isSidebarCollapsed)}
      </aside>

      {/* Mobile Sidebar Backdrop */}
      {isMobileNavOpen && (
        <div
          className="fixed inset-0 z-dropdown bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileNavOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-modal w-sidebar flex flex-col glass-sidebar transform transition-transform duration-normal lg:hidden",
        isMobileNavOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {renderSidebarContent("mobile")}
      </aside>

      {/* Main Content */}
      <div
        className="flex flex-col min-h-screen main-margin-transition"
        style={{ marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024 ? (isSidebarCollapsed ? '64px' : '240px') : undefined }}
      >
        {/* Header */}
<header className="sticky top-0 z-sticky h-header flex items-center bg-background-secondary border-b border-primary/20 px-4 sm:px-6 lg:px-8 shrink-0">
          {/* Sidebar toggle button area — preserved */}
          <button
            type="button"
            className="lg:hidden mr-4 p-2 rounded-button text-gray-400 hover:text-white hover:bg-primary/10 transition-colors duration-normal"
            onClick={() => setIsMobileNavOpen(true)}
            aria-label="Apri menu"
          >
            <ApperIcon name="Menu" className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
<h1
              className="text-heading-lg font-display truncate"
              style={{
                background: 'linear-gradient(135deg, #e9e0ff 0%, #c4b5fd 50%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 10px rgba(167, 139, 250, 0.45))',
              }}
            >
              {location.pathname === "/" && "Dashboard"}
              {location.pathname.startsWith("/spazi") && "Spazi Pubblicitari"}
              {location.pathname.startsWith("/clienti") && "Clienti"}
              {location.pathname.startsWith("/prenotazioni") && "Prenotazioni"}
{location.pathname.startsWith("/timeline") && "Timeline"}
              {location.pathname.startsWith("/pagamenti") && "Pagamenti"}
              {location.pathname.startsWith("/competenze-mensili") && "Competenze Mensili"}
              {location.pathname.startsWith("/preventivi") && "Preventivi"}
              {location.pathname.startsWith("/pubblico") && "Pagina Pubblica"}
              {location.pathname.startsWith("/test-webhook") && "Test Webhook"}
            </h1>
          </div>

          <button
            type="button"
            onClick={handleCopyLink}
            title="Copia link da inviare al cliente"
            className={`ml-3 flex items-center gap-2 px-3 py-2 rounded-button border text-body-sm shrink-0 transition-all duration-normal ${
              copied
                ? "bg-green-500/20 border-green-400/60 text-green-300"
                : "bg-white/5 border-primary/30 text-gray-300 hover:bg-primary/10 hover:border-primary/60 hover:text-white"
            }`}
            style={{ backdropFilter: "blur(12px)" }}
          >
            <ApperIcon name={copied ? "Check" : "Link"} className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">
              {copied ? "Link copiato ✔" : "Copia link pagina pubblica"}
            </span>
          </button>

          <button
            type="button"
            onClick={logout}
            className="ml-4 flex items-center gap-2 px-3 py-2 rounded-button text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-normal shrink-0"
            aria-label="Esci"
          >
            <ApperIcon name="LogOut" className="w-4 h-4" />
            <span className="hidden sm:inline text-body-sm">Esci</span>
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;